import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-hot-toast';
import config, { getApiUrl, getEndpoint } from '../config';

const API_URL = getEndpoint('TESTIMONIAL');

const Testimonial = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [position, setPosition] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('توکن احراز هویت یافت نشد. لطفاً دوباره وارد شوید.');
        window.location.href = '/login';
        return;
      }
      
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(res.data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      if (err.response && err.response.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (err.response && err.response.status === 403) {
        toast.error('توکن نامعتبر است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error('خطا در دریافت نظرات');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.id);
      setTitle(item.title);
      setImage(null);
      setImagePreview(item.image || '');
      setPosition(item.position);
      setCompany(item.company);
      setDescription(item.description);
    } else {
      setEditId(null);
      setTitle('');
      setImage(null);
      setImagePreview('');
      setPosition('');
      setCompany('');
      setDescription('');
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setTitle('');
    setImage(null);
    setImagePreview('');
    setPosition('');
    setCompany('');
    setDescription('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('فقط فایل تصویری مجاز است');
        return;
      }
      if (file.size > 100 * 1024) {
        toast.error('حجم تصویر نباید بیشتر از ۱۰۰ کیلوبایت باشد');
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !position.trim() || !company.trim() || !description.trim()) {
      toast.error('همه فیلدها الزامی است');
      return;
    }
    const formData = new FormData();
    formData.append('title', title);
    formData.append('position', position);
    formData.append('company', company);
    formData.append('description', description);
    if (image) formData.append('image', image);
    try {
      const token = localStorage.getItem('token');
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('نظر ویرایش شد');
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('نظر اضافه شد');
      }
      fetchTestimonials();
      handleClose();
    } catch (err) {
      toast.error('خطا در ذخیره نظر');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این نظر را حذف کنید؟')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('نظر حذف شد');
      fetchTestimonials();
    } catch (err) {
      toast.error('خطا در حذف نظر');
    }
  };

  return (
    <Box sx={{ fontFamily: 'Vazir', direction: 'rtl' }}>
      <Typography variant="h5" sx={{ mb: 2, fontFamily: 'Vazir', fontWeight: 'bold' }}>نظرات مشتریان</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ mb: 2, fontFamily: 'Vazir' }}>
        افزودن نظر
      </Button>
      <TableContainer component={Paper} sx={{ fontFamily: 'Vazir' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="right" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تصویر</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عنوان</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>سمت</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>شرکت</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>توضیحات</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testimonials.map((item) => (
              <TableRow key={item.id}>
                <TableCell align="right">
                  {item.image && <Avatar src={getApiUrl(`/api/media/download?path=?${item.image.split('/').pop()}`)} alt={item.title} sx={{ width: 56, height: 56, margin: 'auto' }} />}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Vazir' }}>{item.title}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Vazir' }}>{item.position}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Vazir' }}>{item.company}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Vazir' }}>{item.description}</TableCell>
                <TableCell align="center">
                  <IconButton color="primary" onClick={() => handleOpen(item)}><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={handleClose} dir="rtl">
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>{editId ? 'ویرایش نظر' : 'افزودن نظر'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="عنوان"
            type="text"
            fullWidth
            value={title}
            onChange={e => setTitle(e.target.value)}
            sx={{ fontFamily: 'Vazir' }}
          />
          <TextField
            margin="dense"
            label="سمت"
            type="text"
            fullWidth
            value={position}
            onChange={e => setPosition(e.target.value)}
            sx={{ fontFamily: 'Vazir' }}
          />
          <TextField
            margin="dense"
            label="شرکت"
            type="text"
            fullWidth
            value={company}
            onChange={e => setCompany(e.target.value)}
            sx={{ fontFamily: 'Vazir' }}
          />
          <TextField
            margin="dense"
            label="توضیحات"
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            sx={{ fontFamily: 'Vazir' }}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 2, fontFamily: 'Vazir' }}
          >
            انتخاب تصویر (حداکثر ۱۰۰ کیلوبایت)
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />
          </Button>
          {imagePreview && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Avatar src={imagePreview} alt="preview" sx={{ width: 80, height: 80, margin: 'auto' }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ fontFamily: 'Vazir' }}>{editId ? 'ذخیره' : 'افزودن'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Testimonial; 